import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Collapse,
  Typography,
  Stack,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton,
  Divider,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Cloud,
  Close,
  Search,
  CheckCircleRounded,
  LinkRounded,
  SendRounded,
  KeyboardArrowUp,
  HourglassTop,
  RadioButtonUnchecked,
} from '@mui/icons-material';
import API from '../../api/api';
import { useGetTutorSessions } from '../../Hooks/useGetTutorSessions';
import { useSelector } from 'react-redux';
import { DateTime } from 'luxon';




// ─── SessionLinkerPanel ───────────────────────────────────────────────────────
/**
 * Replaces <DialogActions> in proctor mode.
 *
 * Props:
 *   proctor            — boolean
 *   onClose            — () => void
 *   onFinalSubmit      — (linkedSession) => void  — called with the selected session
 *   previousSession    — boolean
 *   assessmentId       — number | string
 *   answers            — object  — current answers map (so parent still controls state)
 */
const SessionLinkerPanel = ({
  proctor,
  onClose,
  onFinalSubmit,
  previousSession,
  studentId,
  semesterID,
  answers = {},
  setAutoGrader,
  autoGrader
}) => {
  const [open, setOpen]               = useState(false);
  const [loading, setLoading]         = useState(false);
  const [search, setSearch]           = useState('');
  const [selected, setSelected]       = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError]             = useState('');
  const [locationID, setLocationID] = useState(null);
  const locations = useSelector((state) => state.role.tutor_locations);
  
  const {sessions, loading: loading_sessions, fetchTutorSessions} = useGetTutorSessions();

  // Fetch active sessions when the panel opens
  useEffect(() => {
    if (locationID && semesterID) {
        fetchTutorSessions(locationID, semesterID)
    }
  }, [locationID]);

  const filtered = useMemo(() => {
    let result = sessions;
 
    // 1. Keep only sessions that contain the target student
    if (studentId) {
      result = result.filter(s =>
        Array.isArray(s.students) &&
        s.students.some(st => st.student_id === studentId)
      );
    }
 
    // 2. Keep only sessions for the selected location
    if (locationID) {
      result = result.filter(s => s.location_id === locationID);
    }
    return result;
  }, [sessions, locationID]);

  const handleOpen = () => {
    setOpen(true);
    setSelected(null);
  };

  const handleClose = () => {
    setOpen(false);
    setSelected(null);
    setSearch('');
    setError('');
  };

  const handleSubmit = async () => {
    if (!selected) return;
    setSubmitLoading(true);
    try {
      await onFinalSubmit?.(selected);
      handleClose();
    } catch {
      setError('Submission failed. Please try again.');
    } finally {
      setSubmitLoading(false);
    }
  };

  function formatSessionDate(sessionDate) {
    if (!sessionDate) return '—';
    return DateTime.fromISO(sessionDate.replace('Z', ''), { zone: 'local' }).toLocaleString();
  }

  function formatSessionTime(startTime) {
    if (!startTime) return '';
    try {
      return (
        DateTime.fromFormat(startTime, 'HH:mm:ss').toFormat('h:mm a') ||
        DateTime.fromFormat(startTime, 'HH:mm').toFormat('h:mm a')
      );
    } catch {
      return startTime;
    }
  }

  const answeredCount = Object.keys(answers).length;

  // ── Non-proctor: simple close button ─────────────────────────────────────
  if (!proctor) {
    return (
      <Box
        sx={{
          px: 3,
          py: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <Button onClick={onClose} variant="text" sx={{ textTransform: 'none' }}>
          Close
        </Button>
      </Box>
    );
  }

  // ── Proctor mode ──────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        borderTop: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        // Animate height when panel opens
        transition: 'all 0.25s ease',
      }}
    >
      {/* ── Expandable session-linker panel ── */}
      <Collapse in={open} unmountOnExit>
        <Box sx={{ px: 3, pt: 2.5, pb: 1 }}>

          {/* Panel header */}
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <LinkRounded fontSize="small" color="primary" />
              <Typography variant="subtitle2" fontWeight={700}>
                Link to a session
              </Typography>
              {selected && (
                <Chip
                  label={`${selected.program_name}`}
                  size="small"
                  color="primary"
                  onDelete={() => setSelected(null)}
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              )}
            </Stack>
            <IconButton size="small" onClick={handleClose} sx={{ p: 0.5 }}>
              <KeyboardArrowUp fontSize="small" />
            </IconButton>
          </Stack>

          <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
            Select the student session this submission belongs to, then press Submit.
          </Typography>

          <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          sx={{ mb: 1, width: { xs: '100%', sm: 'auto' } }}
        >
          {/* Location */}
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 160 } }}>
            <InputLabel id="sg-location-label">Location</InputLabel>
            <Select
              labelId="sg-location-label"
              value={locationID || ''}
              label="Location"
              onChange={(e) => setLocationID(e.target.value)}
              onTouchEnd={(e) => setLocationID(e.target.value)}
            >
              {locations.map((loc) => (
                <MenuItem key={loc.id} value={loc.id}>
                  <Typography variant="body2">{loc.location_name}</Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel control={<Checkbox value={autoGrader} onClick={() => setAutoGrader((prev) => !prev)} />} label="Auto grader" />
        </Stack>

          {/* Error */}
          {error && (
            <Alert severity="error" variant="outlined" sx={{ mb: 1.5, py: 0.5 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {/* Session table */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <TableContainer
              sx={{
                maxHeight: 250,
                overflowY: 'auto',
                border: '0.5px solid',
                borderColor: 'divider',
                borderRadius: 2,
                mb: 1,
              }}
            >
              <Table size="small" stickyHeader sx={{ tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow>
                    {[
                      { label: 'Date',    width: '30%' },
                      { label: 'time',    width: '22%' },
                      { label: 'Program', width: undefined },
                      { label: 'Count', width: undefined },
                      { label: 'OK',        width: 40 },
                    ].map(col => (
                      <TableCell
                        key={col.label}
                        width={col.width}
                        sx={{
                            bgcolor: 'background.paper',
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            fontSize: '0.68rem',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            color: 'text.secondary',
                            py: 0.75,
                            borderBottom: '0.5px solid',
                            borderColor: 'divider',
                        }}
                      >
                        {col.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.disabled', fontSize: '0.8rem' }}>
                        {locationID ? 'No sessions found for this student' : 'Select a location to load sessions'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filtered.map(session => {
                      const isSelected = selected?.session_id === session.session_id;
                      return (
                        <TableRow
                          key={session.session_id}
                          hover
                          onClick={() => setSelected(isSelected ? null : session)}
                          sx={{
                            cursor: 'pointer',
                            bgcolor: isSelected ? 'primary.50' : 'inherit',
                            '&:hover': { bgcolor: isSelected ? 'primary.100' : 'action.hover' },
                            '&:last-child td': { borderBottom: 0 },
                            transition: 'background 0.12s',
                          }}
                        >
                          {/* Date */}
                          <TableCell sx={{ py: 0.75 }}>
                            <Typography variant="body2" fontWeight={isSelected ? 600 : 400} noWrap>
                              {formatSessionDate(session.session_date)}
                            </Typography>
                          </TableCell>
 
                          {/* Start time */}
                          <TableCell sx={{ py: 0.75 }}>
                            <Typography
                              variant="caption"
                              sx={{ fontFamily: 'monospace', fontWeight: 600, letterSpacing: '0.04em' }}
                            >
                              {formatSessionTime(session.start_time)}
                            </Typography>
                          </TableCell>
 
                          {/* Program */}
                          <TableCell sx={{ py: 0.75 }}>
                            <Typography variant="body2" noWrap title={session.program_name}>
                              {session.program_name || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 0.75 }}>
                            <Typography
                              variant="caption"
                              sx={{ fontFamily: 'monospace', fontWeight: 600, letterSpacing: '0.04em' }}
                            >
                              {session.student_count}
                            </Typography>
                          </TableCell>
 
                          {/* Select indicator */}
                          <TableCell sx={{ py: 0.75 }} align="center">
                            {isSelected && (
                              <CheckCircleRounded fontSize="small" color="primary" />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

          )}

          {/* Answered count summary */}
          <Typography variant="caption" color="text.secondary" display="block" mb={1}>
            Submitting <strong>{answeredCount}</strong> answered question{answeredCount !== 1 ? 's' : ''}.
          </Typography>
        </Box>

        <Divider />
      </Collapse>

      {/* ── Footer action row — always visible ── */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 3, py: 1.75, gap: 1 }}
      >
        {/* Left: save / open-panel trigger */}
        {!open ? (
          <Button
            onClick={handleOpen}
            variant="contained"
            color={previousSession ? 'success' : 'primary'}
            startIcon={<Cloud />}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {previousSession ? 'Session saved ✓' : 'Save session'}
          </Button>
        ) : (
          // Final submit — only active once a session is selected
          <Tooltip
            title={!selected ? 'Select a session above first' : ''}
            disableHoverListener={!!selected}
          >
            <span>
              <Button
                onClick={handleSubmit}
                variant="contained"
                color="success"
                disabled={!selected || submitLoading}
                startIcon={submitLoading ? <CircularProgress size={16} color="inherit" /> : <SendRounded />}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                {submitLoading ? 'Submitting…' : `Submit ` }
              </Button>
            </span>
          </Tooltip>
          
        )}

        {/* Right: close */}
        <Button
          onClick={open ? handleClose : onClose}
          variant="text"
          sx={{ textTransform: 'none', color: 'text.secondary' }}
        >
          {open ? 'Cancel' : 'Close'}
        </Button>
      </Stack>
    </Box>
  );
};

export default SessionLinkerPanel;